// [Proposition: timeline-taches] Client for /api/timeline-taches:
// a project's tasks on the week grid, and each task's change log that the PM
// validates in the meeting.

import type { DateChange } from '../../types';

const BASE = '/api/timeline-taches';

export interface TimelineTask {
  id: number;
  title: string;
  column_id: number;
  column_label: string;
  is_done: boolean;
  assignee_name: string | null;
  collaborator_names: string[];
  milestone_id: string | null;
  blocked_reason: string | null;
  is_private: boolean;
  can_open: boolean;
  created_on: string;          // 'YYYY-MM-DD'
  due_date: string | null;     // 'YYYY-MM-DD'
  planned_at: string | null;   // ISO timestamp
  due_date_history: DateChange[];
  planned_at_history: DateChange[];
  done_on: string | null;      // 'YYYY-MM-DD', last move to Done
  blocked_periods: { from: string; to: string | null }[]; // 'YYYY-MM-DD'; to null while still blocked
  pending_changes: number;     // changes not validated by the PM yet
}

export interface TimelineData {
  project: { id: number; name: string; project_code: string | null };
  milestones: { id: string; name: string; estimated_end: string | null }[];
  tasks: TimelineTask[];
  people: string[];
  canValidate: boolean;
}

export type ChangeType =
  | 'created' | 'title' | 'description' | 'status' | 'blocked_reason'
  | 'due_date' | 'planned_at' | 'assignee' | 'collaborator_added' | 'collaborator_removed'
  | 'privacy';

export interface TaskChangeEntry {
  id: number;
  change_type: ChangeType;
  old_value: string | null;
  new_value: string | null;
  actor_name: string | null;
  changed_at: string;
  validated_at: string | null;
  validated_by: string | null;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

export const timelineApi = {
  getProject: (projectId: number) => request<TimelineData>(`${BASE}/projects/${projectId}`),
  getChanges: (taskId: number) => request<{ changes: TaskChangeEntry[]; canValidate: boolean }>(`${BASE}/tasks/${taskId}/changes`),
  validate: (taskId: number, ids: number[]) =>
    request<{ validated: number }>(`${BASE}/tasks/${taskId}/changes/validate`, { method: 'POST', body: JSON.stringify({ ids }) }),
};
